import React, { useState } from 'react';
import { Alert, Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import unifiedApi from '../../../utils/unifiedApi';
import { useFeatures } from '../../../contexts/FeatureContext';
import { FEATURE_DEFINITIONS } from './featureDefinitions';
import FeatureOffConfirmDialog from './FeatureOffConfirmDialog';

/** Organization-wide feature switches. Leagues follow these unless they override them in League Settings. */
const OrganizationFeatureSettings = ({ canEdit = false }) => {
  const { isEnabled, setFlags } = useFeatures();
  const [savingKey, setSavingKey] = useState(null);
  const [pendingOff, setPendingOff] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const save = async (feature, enabled) => {
    setSavingKey(feature.key);
    setMessage({ type: '', text: '' });
    try {
      const flags = await unifiedApi.updateOrganizationFeatures({ [feature.key]: enabled });
      setFlags(flags);
      setMessage({ type: 'success', text: `${feature.label} turned ${enabled ? 'on' : 'off'}.` });
    } catch {
      setMessage({ type: 'error', text: `Failed to update ${feature.label}.` });
    } finally {
      setSavingKey(null);
    }
  };

  const handleToggle = (feature, checked) => {
    if (checked) {
      save(feature, true);
    } else {
      setPendingOff(feature);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Turn features on or off for your whole organization. Each league follows these settings unless it is
        changed in that league&apos;s settings.
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={2}>
        {FEATURE_DEFINITIONS.map((feature) => (
          <Box key={feature.key}>
            <FormControlLabel
              control={(
                <Switch
                  checked={isEnabled(feature.key)}
                  onChange={(e) => handleToggle(feature, e.target.checked)}
                  disabled={!canEdit || savingKey === feature.key}
                  inputProps={{ 'aria-label': feature.label }}
                />
              )}
              label={feature.label}
            />
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {feature.description}
            </Typography>
          </Box>
        ))}
      </Stack>

      <FeatureOffConfirmDialog
        feature={pendingOff}
        scopeLabel="for your organization"
        onCancel={() => setPendingOff(null)}
        onConfirm={() => {
          const feature = pendingOff;
          setPendingOff(null);
          save(feature, false);
        }}
      />
    </Paper>
  );
};

export default OrganizationFeatureSettings;
